import React, { useEffect, useState } from "react";
import liff from "@line/liff";
import Header from "./components/Header";
import ProductCard from "./components/ProductCard";
import DeliverAddressCard from "./components/DeliverAddressCard";
import DeliverTimeCard from "./components/DeliverTimeCard";
import TaxInvoiceCard from "./components/TaxInvoiceCard";
import Postscript from "./components/Postscript";
import ContactPhone from "./components/ContactPhone";
import OrderNote from "./components/OrderNote";
import DraftNotFoundView from "./components/DraftNotFoundView";
import { CalendarDay, Order, SavedAddress } from "./utils/type";
import {
  DraftOrderResponse,
  getDraftOrder,
  mapDraftToOrder,
  updateDraftOrder,
  getAvailableSlots,
  AvailableSlotsDay,
  getGasPriceList,
  GasPriceItem,
  cancelDraftOrder,
  getCustomerAddressList,
  mapCustomerAddressToSaved,
  getQuickOrderErrorMessage,
  isSelectedSlotPastForToday,
  isSelectedDateClosed,
  isDeliveryDayClosed,
  getAllDraftIds,
} from "./utils/mockApi";
import { buildDraftUpdatePayload } from "./utils/draftPayload";
import { validateTaxIdNumber } from "@components/form/validate/validateTaxIdNumber";
import { validateInvoiceCarrier } from "@components/form/validate/validateInvoiceCarrier";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { Button, View } from "tamagui";
import { colors } from "@styles/colors";
import * as basicStyle from "@styles/index";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { appToast } from "@components/Toast";
import { useAuthReady } from "@provider/LineLoginProvider";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import api from "@services/axios/apiManager";
import OrderConfirmFlow, { ConfirmOrderViewModel } from "./OrderConfirmFlow";
import { gasTypeDisplayName } from "@utils/func/gasTitle";
import { flushBeforeClose } from "@lib/tracker";

const initOrder: Order = {
  items: [{ id: 1, weight: "20", type: "gas", quantity: 1 }],
  addressId: "1",
  deliveryType: "scheduled",
  deliveryDate: null,
  deliverySlotId: null,
  deliverySlotSource: null,
  deliverySlotLabel: null,
  taxId: "",
  carrier: "",
  phone: "0912-345-678",
  note: "",
};

declare global {
  interface Window {
    L?: any;
  }
}

function StepDot({ active, current, done, label, num }: { active?: boolean; current?: boolean; done?: boolean; label: string; num: string }) {
  const bg = done ? colors.primary : active ? colors.primary : "#E5E7EB";
  const color = done || active ? "#fff" : "#999";
  const labelColor = done || active ? colors.primary : "#999";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: bg,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          boxShadow: current ? "0 0 0 4px rgba(37, 99, 235,0.2)" : "none",
          transition: "all 0.3s",
        }}>
        {num}
      </div>
      <span
        style={{
          fontSize: 11,
          color: labelColor,
          fontWeight: current ? 700 : 500,
          whiteSpace: "nowrap",
        }}>
        {label}
      </span>
    </div>
  );
}
const ArrowLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function QuickOrder() {
  const authReady = useAuthReady();
  const { lineAccessToken } = useLineAuth();
  const { isLoading: isAuthLoading } = useUtilsStore();

  const { orderID } = useParams();
  const [searchParams] = useSearchParams();
  const draftId = orderID ?? searchParams.get("draft_id") ?? undefined;
  const navigate = useNavigate();
  const isEditMode = Boolean(orderID);
  const isCreate = !isEditMode;

  /**
   * 修改完整訂單的兩種 endpoint 流程（僅影響「確認修改」按鈕行為）：
   *
   * 1) 直接送出、不進三步驟確認：
   *    URL: /quickOrder/:orderID?skip_confirm=1 或 /quickOrder/:orderID/skip_confirm
   *    行為：進入編輯頁 → 按下「確認修改」→ 驗證通過後直接呼叫 update API → 成功後關閉並回 Line。
   *
   * 2) 需經三步驟確認：
   *    URL: /quickOrder/:orderID（無 skip_confirm 或路徑非 /skip_confirm）
   *    行為：進入編輯頁 → 按下「確認修改」→ 進入 OrderConfirmFlow 三步驟確認 → 完成後送出並可返回 Line。
   */
  const { pathname } = useLocation();
  const skipConfirm = searchParams.get("skip_confirm") === "1" || pathname.endsWith("/skip_confirm");

  const [showAddressInput, setShowAddressInput] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [calendarDays, setCalendarDays] = useState<(CalendarDay | null)[]>([]);
  const [mapKey, setMapKey] = useState<number>(0);
  const [isFetchingDraft, setIsFetchingDraft] = useState(false);
  const [draftData, setDraftData] = useState<DraftOrderResponse | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlotsDay[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [draftNotFound, setDraftNotFound] = useState(false);
  const [gasPriceList, setGasPriceList] = useState<GasPriceItem[]>([]);

  const [order, setOrder] = useState<Order>({ ...initOrder });

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [jwtEnsured, setJwtEnsured] = useState(false);
  const [hasAutoSelectedSlot, setHasAutoSelectedSlot] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowContent(true), 200);
    return () => clearTimeout(t);
  }, []);

  /**
   * 確保在進入快速訂單前已取得 jwtToken：
   * - 若已存在 jwtToken：直接標記完成
   * - 若沒有 jwtToken 但有 lineAccessToken：再呼叫一次 /app-api/line/login 換 jwt
   * - 若兩者皆無：放行，後續 API 會依原本錯誤機制處理（例如 401）
   */
  useEffect(() => {
    if (!authReady || jwtEnsured) return;

    const ensureJwtToken = async () => {
      if (typeof window === "undefined") {
        setJwtEnsured(true);
        return;
      }

      const existingJwt = sessionStorage.getItem("jwtToken");
      if (existingJwt) {
        setJwtEnsured(true);
        return;
      }

      if (!lineAccessToken) {
        setJwtEnsured(true);
        return;
      }

      try {
        interface LoginResponse {
          data: {
            jwtToken: string;
          };
        }
        const res = await api.post<LoginResponse>("/app-api/line/login", { accessToken: lineAccessToken }, { isAuth: false });
        const jwtToken = res.data.data.jwtToken;
        if (jwtToken) {
          sessionStorage.setItem("jwtToken", jwtToken);
        }
      } catch (_err) {
        // 若補發 jwt 失敗，後續真正的業務 API 會回傳更明確的錯誤
      } finally {
        setJwtEnsured(true);
      }
    };

    void ensureJwtToken();
  }, [authReady, lineAccessToken, jwtEnsured]);

  /** ----------------------
   *  日曆生成
   ------------------------*/
  useEffect(() => {
    generateCalendar();
  }, [currentMonth, availableSlots]);

  /** ----------------------
   *  載入可預約時段和商品價格列表（需等 Line 授權完成才有 jwtToken）
   ------------------------*/
  useEffect(() => {
    if (!authReady || !jwtEnsured) return;
    const loadData = async () => {
      setIsLoadingSlots(true);
      try {
        const [slots, prices] = await Promise.all([getAvailableSlots(), getGasPriceList()]);
        setAvailableSlots(slots);
        setGasPriceList(prices);
        if (slots.length > 0) {
          const firstDate = new Date(slots[0].delivery_date);
          setCurrentMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
        }
      } catch (err) {
        // 不顯示錯誤提示，因為這不是關鍵功能
      } finally {
        setIsLoadingSlots(false);
      }
    };
    loadData();
  }, [authReady, jwtEnsured]);

  /** ----------------------
   *  取得客戶地址列表（地址選單來源）
   ------------------------*/
  useEffect(() => {
    if (!authReady || !jwtEnsured) return;
    const loadAddresses = async () => {
      try {
        const list = await getCustomerAddressList();
        const fromApi = list.map(mapCustomerAddressToSaved);
        setSavedAddresses((prev) => {
          const apiIds = new Set(fromApi.map((a) => a.id));
          const onlyInPrev = prev.filter((a) => !apiIds.has(a.id));
          return [...onlyInPrev, ...fromApi];
        });
        if (!isEditMode && fromApi.length > 0) {
          setOrder((prev) => ({ ...prev, addressId: fromApi[0].id }));
        }
      } catch (_err) {
        // 失敗時維持空列表，使用者仍可新增地址
      }
    };
    loadAddresses();
  }, [authReady, jwtEnsured, isEditMode]);

  /** ----------------------
   *  API 讀取草稿 (edit 模式)。等最外層登入檢查結束後才開始，避免與 auth loading overlay 重疊。
   ------------------------*/
  useEffect(() => {
    if (isAuthLoading || !authReady || !jwtEnsured || !isEditMode || !draftId) return;

    const controller = new AbortController();
    const { signal } = controller;
    let isMounted = true;
    setDraftNotFound(false);
    setIsFetchingDraft(true);

    getDraftOrder(draftId, signal)
      .then((draft) => {
        if (!isMounted) return;
        setDraftNotFound(false);
        setDraftData(draft);
        const mapped = mapDraftToOrder(draft);
        setOrder(mapped.order);
        // 草稿的地址併入列表，不覆蓋既有 API 地址列表
        setSavedAddresses((prev) => {
          const draftAddrs = mapped.savedAddresses;
          if (!draftAddrs.length) return prev;
          const draftAddr = draftAddrs[0];
          if (prev.some((a) => a.id === draftAddr.id)) return prev;
          return [draftAddr, ...prev];
        });
        setShowAddressInput(false);
        setMapKey((prev) => prev + 1);
        // 如果有 deliveryDate，使用對應的月份；否則不改變月份（讓用戶根據 API 數據選擇）
        if (mapped.currentMonth) {
          setCurrentMonth(mapped.currentMonth);
        }
        // 立即配送時，不改變月份，保持當前顯示
      })
      .catch((err) => {
        // 由 AbortController 取消時不顯示錯誤提示
        const isAborted = err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
        if (!isAborted && isMounted) {
          setDraftNotFound(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsFetchingDraft(false);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isAuthLoading, authReady, jwtEnsured, isEditMode, draftId]);

  /**
   * 根據草稿的 delivery_time_stamp，自動對應並預選配送時段（僅「預約配送」且有時間時）：
   * - 僅在編輯模式、草稿為預約配送、有有效 delivery_time_stamp 且後端未回 slot_id/slot_source 時啟用
   * - 使用 availableSlots 中對應日期的 slot，找出時間區間涵蓋該時間點的那一筆
   * - 若當日無資料、無符合 slot、額滿或已過期，則不預選
   */
  useEffect(() => {
    if (!isEditMode) return;
    if (!draftData) return;
    if (hasAutoSelectedSlot) return;
    if (!availableSlots.length) return;

    const info = draftData.order_infos;
    // 立即配送（含 mapDraftToOrder 已推斷為 immediate 的情況）→ 不自動選時段，避免以舊時間戳記覆蓋
    if (info?.delivery_type_enum === "立即配送" || order.deliveryType === "immediate") {
      setHasAutoSelectedSlot(true);
      return;
    }

    // 若後端已回傳 slot_id/slot_source，沿用 mapDraftToOrder 的結果，不再覆寫
    const hasBackendSlot = typeof info?.slot_id === "number" && (info.slot_source === "weekly" || info.slot_source === "special");
    if (hasBackendSlot) {
      setHasAutoSelectedSlot(true);
      return;
    }

    const deliveryTimestamp = info?.delivery_time_stamp;
    if (!deliveryTimestamp || typeof deliveryTimestamp !== "string") return;
    const trimmed = deliveryTimestamp.trim();
    if (!trimmed) return;

    const ts = new Date(trimmed);
    if (Number.isNaN(ts.getTime())) return;

    const year = ts.getFullYear();
    const month = String(ts.getMonth() + 1).padStart(2, "0");
    const day = String(ts.getDate()).padStart(2, "0");
    const hours = ts.getHours();
    const minutes = ts.getMinutes();
    const targetMinutes = hours * 60 + minutes;
    const dateStr = `${year}-${month}-${day}`;

    const dayData = availableSlots.find((d) => d.delivery_date === dateStr);
    if (!dayData || dayData.is_closed_all_day || !dayData.slots?.length) return;

    const matchedSlot = dayData.slots.find((slot) => {
      const [sh, sm] = slot.start_time.split(":").map(Number);
      const [eh, em] = slot.end_time.split(":").map(Number);
      const startMinutes = (sh || 0) * 60 + (sm || 0);
      const endMinutes = (eh || 0) * 60 + (em || 0);
      return targetMinutes >= startMinutes && targetMinutes < endMinutes;
    });

    if (!matchedSlot) return;

    const availableCapacity = matchedSlot.available_capacity ?? 0;
    if (availableCapacity <= 0) return;
    if (isSelectedSlotPastForToday(availableSlots, dateStr, matchedSlot.slot_id)) return;

    setOrder((prev) => ({
      ...prev,
      deliveryType: "scheduled",
      deliveryDate: dateStr,
      deliverySlotId: matchedSlot.slot_id,
      deliverySlotSource: matchedSlot.slot_source,
      deliverySlotLabel: `${matchedSlot.start_time} - ${matchedSlot.end_time}`,
    }));
    setHasAutoSelectedSlot(true);
  }, [isEditMode, draftData, availableSlots, hasAutoSelectedSlot, order.deliveryType]);

  const formatDate = (date: Date): string => {
    // 使用本地時間格式化，避免時區問題
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const hasAvailableSlots = (dateStr: string): boolean => {
    // 完全按照 API 數據判斷，不使用 mock 數據
    if (availableSlots.length > 0) {
      const dayData = availableSlots.find((day) => day.delivery_date === dateStr);
      if (dayData) {
        if (dayData.is_closed_all_day) {
          return false;
        }
        // 檢查是否有任何 slot 的 available_capacity > 0
        return dayData.slots.some((slot) => slot.available_capacity > 0);
      }
      // 如果 API 中沒有該日期，返回 false（不可選）
      return false;
    }

    // 如果還沒有載入 availableSlots 數據，返回 false（不可選）
    return false;
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();

    const days: (CalendarDay | null)[] = [];

    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);

      // 完全按照 API 數據判斷
      const dayData = availableSlots.find((d) => d.delivery_date === dateStr);

      // 如果 API 中有該日期的數據：依 is_closed_all_day 簡單判斷公休，其餘看是否有可預約 slot
      if (dayData) {
        const isClosed = isDeliveryDayClosed(availableSlots, dateStr);
        const isAvailable = !isClosed && dayData.slots.some((slot) => slot.available_capacity > 0);
        const isPast = false;

        days.push({
          day,
          date: dateStr,
          fullDate: date,
          isAvailable,
          isPast,
          isClosed,
        });
      } else {
        // 如果 API 中沒有該日期，視為不可選
        const isPast = date < today;
        days.push({
          day,
          date: dateStr,
          fullDate: date,
          isAvailable: false,
          isPast,
          isClosed: false, // API 中沒有數據的日期不顯示「休」
        });
      }
    }

    setCalendarDays(days);
  };

  /** ----------------------
   *  取消訂單
   ------------------------*/
  const handleCancel = async () => {
    if (!orderID) {
      appToast.error("找不到草稿單號，請重新進入頁面。");
      return;
    }

    if (!window.confirm("確定要取消此草稿訂單嗎？")) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelDraftOrder(orderID);
      appToast.success("訂單已取消成功！");
      navigate("/quickOrder/drafts");
    } catch (err: any) {
      appToast.error(getQuickOrderErrorMessage(err));
    } finally {
      setIsCancelling(false);
    }
  };

  /** ----------------------
   *  真正送出訂單（給確認頁呼叫）
   *  不自動關閉／導頁，由完成頁的「返回 LINE 聊天室」處理關閉行為
   ------------------------*/
  const submitOrder = async () => {
    if (isCreate) {
      // 新增模式目前仍使用前端流程，這裡先只顯示成功提示
      appToast.success("新增訂單成功！");
      return;
    }

    if (!draftId) {
      appToast.error("找不到草稿單號，請重新進入頁面。");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildDraftUpdatePayload({
        order,
        savedAddresses,
        draftData,
      });

      await updateDraftOrder(draftId, payload);
      appToast.success("訂單已更新成功！");
    } catch (err: any) {
      appToast.error(getQuickOrderErrorMessage(err));
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  /** 完成頁「返回 LINE 聊天室」：才真正關閉視窗或導回草稿列表 */
  const handleBackToLine = () => {
    try {
      if (liff.isInClient()) {
        flushBeforeClose(); // 離頁前強制 flush 佇列中的事件，避免 LIFF 關窗把最後幾筆事件丟掉
        liff.closeWindow();
      } else {
        navigate("/quickOrder/drafts");
      }
    } catch {
      navigate("/quickOrder/drafts");
    }
  };

  /** 統編、載具、聯絡電話填寫正確才可送出 */
  const isInvoiceOrPhoneInvalid = (() => {
    const taxId = order.taxId.trim();
    if (taxId.length > 0) {
      if (taxId.length !== 8 || !validateTaxIdNumber(taxId)) return true;
    }
    const carrier = order.carrier.trim();
    if (carrier.length > 0 && !validateInvoiceCarrier(carrier)) return true;
    const phone = order.phone.trim();
    if (phone.length === 0 || !validateTaiwanPhone(phone)) return true;
    return false;
  })();

  /** ----------------------
   *  提交按鈕：驗證通過後，依 skip_confirm 決定直接送出或進入三步驟確認頁
   ------------------------*/
  const handleSubmit = async () => {
    // 統編／載具／聯絡電話未填寫正確不送出
    if (isInvoiceOrPhoneInvalid) {
      appToast.error("請正確填寫統編、載具或訂單聯絡電話後再送出。");
      return;
    }
    // 驗證：如果選擇預約配送，必須選擇日期和時段
    if (order.deliveryType === "scheduled") {
      if (!order.deliveryDate && (!order.deliverySlotId || !order.deliverySlotSource)) {
        appToast.error("請選擇預約配送的日期與時段。");
        return;
      }
      if (!order.deliveryDate) {
        appToast.error("請先選擇預約配送的日期。");
        return;
      }
      if (!order.deliverySlotId || !order.deliverySlotSource) {
        appToast.error("請選擇預約配送的時段。");
        return;
      }
      /*
       * 選擇的配送時間已過期或不可用 — 兩層防呆：
       * 第一層（UI）：DeliverTimeCard 日曆依 API 的 is_closed_all_day、available_capacity 與「當日 end_time 已過」，
       *   將公休日、額滿時段、已過時段設為不可選（disabled），使用者無法點選。
       * 第二層（送出前）：以下檢查公休日與當日已過時段，若不符合則 toast 並 return，不送出。
       */
      if (isSelectedDateClosed(availableSlots, order.deliveryDate)) {
        appToast.error("所選日期為公休日，請選擇其他日期。");
        return;
      }
      if (isSelectedSlotPastForToday(availableSlots, order.deliveryDate, order.deliverySlotId)) {
        appToast.error("所選配送時段已過期，請重新選擇。");
        return;
      }
    }

    // 送出前再做一次草稿存在性檢查：
    // 避免使用者在確認流程停留太久，草稿已過期仍進入下一步。
    if (isEditMode && draftId) {
      try {
        const draftIds = await getAllDraftIds();
        if (!draftIds.includes(draftId)) {
          setDraftNotFound(true);
          appToast.error("此草稿訂單已過期，請重新一鍵下單。");
          return;
        }
      } catch {
        // 檢查失敗時不中斷既有流程，最終仍由送出 API 作為準確判斷。
      }
    }

    // 驗證通過 → 依 endpoint 決定：直接送出或進入三步驟確認頁
    if (skipConfirm) {
      try {
        await submitOrder();
        handleBackToLine();
      } catch {
        // submitOrder 內已 toast 錯誤，不重複提示
      }
      return;
    }
    setShowConfirm(true);
  };

  /** 僅 order_status === '待確認' 時可編輯 */
  const orderStatus = draftData?.order_infos?.order_status;
  const isDraftNotEditable = isEditMode && orderStatus != null && orderStatus !== "待確認";

  /** ----------------------
   *  UI
   ------------------------*/

  if (showConfirm) {
    const selectedAddress = savedAddresses.find((addr) => addr.id === order.addressId);
    const addressText = selectedAddress?.address || "尚未選擇地址";

    const isImmediate = order.deliveryType === "immediate";
    const deliveryMethodLabel = isImmediate ? "立即配送" : "預約配送";
    const deliveryTimeLabel = isImmediate
      ? "盡快送達（實際以瓦斯行安排為準）"
      : order.deliveryDate
        ? `${order.deliveryDate}${order.deliverySlotLabel ? ` / ${order.deliverySlotLabel}` : ""}`
        : "尚未選擇";

    const confirmOrder: ConfirmOrderViewModel = {
      orderId: draftData?.order_infos?.original_order_id ?? draftId ?? null,
      createdAt: draftData?.created_at ?? null,
      items: order.items.map((item) => ({
        // 液化氣／丙烷 不顯示氣別字樣
        name: gasTypeDisplayName(item.gasType, "桶裝瓦斯"),
        weight: `${item.weight}KG`,
        type: gasTypeDisplayName(item.gasType, item.type === "gas" ? "常用" : "瓦斯品項"),
        qty: item.quantity,
      })),
      delivery: {
        address: addressText,
        method: deliveryMethodLabel,
        estimatedTime: deliveryTimeLabel,
        note: !isImmediate && !order.deliveryDate ? "請返回修改頁選擇預約時間" : undefined,
      },
      contact: { phone: order.phone },
      invoice: { taxId: order.taxId, carrier: order.carrier },
      note: order.note,
      businessHours: undefined,
    };

    return <OrderConfirmFlow onBack={() => setShowConfirm(false)} onConfirm={submitOrder} order={confirmOrder} onBackToLine={handleBackToLine} />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* 僅在「最外層登入檢查已結束」時才顯示頁內載入，避免與 UtilsProvider 的 Loading overlay 重疊 */}
      {(!authReady || !jwtEnsured) && !isAuthLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 10001 }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="text-sm font-medium text-gray-700">載入中...</span>
          </div>
        </div>
      )}
      {/* Loading Modal - 載入草稿資料（僅在登入檢查結束後顯示，避免與最外層 overlay 重疊） */}
      {isEditMode && isFetchingDraft && !isAuthLoading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-sm font-medium text-gray-700">正在載入草稿資料...</span>
          </div>
        </div>
      )}

      {/* 找不到草稿訂單 - 頁面顯示 */}
      {isEditMode && draftNotFound && !isFetchingDraft && <DraftNotFoundView onBackToDrafts={() => navigate("/quickOrder/drafts")} />}

      {/* Loading Modal - 更新訂單 */}
      {isSubmitting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-sm font-medium text-gray-700">正在更新訂單...</span>
          </div>
        </div>
      )}

      {/* Loading Modal - 取消訂單 */}
      {isCancelling && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-sm font-medium text-gray-700">正在取消訂單...</span>
          </div>
        </div>
      )}

      {/* 訂單不可修改（order_status 非「待確認」） */}
      {isDraftNotEditable && (
        <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 py-8">
          <Header title="修改訂單內容" />
          <p className="text-gray-600 text-center mb-6">此訂單無法修改</p>
          <Button {...basicStyle.ShareBlueButton} className="primary-bg-gradient" color="#FFF" size="$5" onPress={() => navigate("/quickOrder/drafts")}>
            返回草稿列表
          </Button>
        </div>
      )}

      {/* Scrollable Content（找不到草稿或不可編輯時不顯示表單） */}
      {!(isEditMode && draftNotFound) && !isDraftNotEditable && (
        <div className="flex-1 overflow-y-auto">
          {/* Leaflet CSS from cdnjs */}
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css" />
          {/* Header */}
          <div
            className="primary-bg-gradient"
            style={{
              padding: "0 0 24px",
              position: "relative",
            }}>
            <div
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0.06,
                backgroundImage:
                  "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            {/* Top bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "14px 16px",
                position: "relative",
                zIndex: 2,
              }}>
              <span
                style={{
                  flex: 1,
                  textAlign: "center",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}>
                雲端桶裝瓦斯服務
              </span>
            </div>
            {/* Title */}
            <div
              style={{
                textAlign: "center",
                padding: "8px 24px 0",
                position: "relative",
                zIndex: 2,
              }}>
              <h1
                style={{
                  color: "#fff",
                  fontSize: 22,
                  fontWeight: 800,
                  margin: "0 0 6px",
                }}>
                {isEditMode ? "修改訂單內容" : "選擇配送時間"}
              </h1>
              <p
                style={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: 13,
                  margin: 0,
                }}>
                修改您的瓦斯訂單內容
              </p>
            </div>
          </div>

          {/* Step indicator */}
          <div
            style={{
              margin: "-12px 16px 16px",
              background: "#fff",
              borderRadius: 14,
              padding: "14px 20px",
              boxShadow: "0 4px 16px rgba(37, 99, 235,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              gap: 0,
              animation: showContent ? "slideUp 0.4s ease forwards" : "none",
              opacity: showContent ? 1 : 0,
            }}>
            <StepDot active current label="修改訂單" num="1" />
            <div
              style={{
                width: 40,
                height: 2,
                background: colors.primary,
                margin: "0 4px",
                marginBottom: 18,
              }}
            />
            <StepDot label="確認內容" num="2" />
            <div
              style={{
                width: 40,
                height: 2,
                background: "#E5E7EB",
                margin: "0 4px",
                marginBottom: 18,
              }}
            />
            <StepDot label="完成" num="3" />
          </div>
          {/* <Header title={isEditMode ? "修改訂單內容" : "選擇配送時間"} /> */}

          <div className="max-w-md mx-auto px-4 py-4 space-y-4" style={{ marginBottom: "5.5em" }}>
            {/* 只有新增模式 */}
            {isCreate && (
              <DeliverTimeCard
                setCurrentMonth={setCurrentMonth}
                currentMonth={currentMonth}
                calendarDays={calendarDays}
                order={order}
                setOrder={setOrder}
                availableSlots={availableSlots}
              />
            )}

            {/* 修改模式：顯示完整畫面 */}
            {isEditMode && (
              <>
                <ProductCard order={order} setOrder={setOrder} gasPriceList={gasPriceList} />

                <DeliverAddressCard
                  lineAccessToken={lineAccessToken ?? undefined}
                  savedAddresses={savedAddresses}
                  setOrder={setOrder}
                  setShowAddressInput={setShowAddressInput}
                  setMapKey={setMapKey}
                  setSavedAddresses={setSavedAddresses}
                  showAddressInput={showAddressInput}
                  mapKey={mapKey}
                  order={order}
                />

                <DeliverTimeCard
                  setCurrentMonth={setCurrentMonth}
                  currentMonth={currentMonth}
                  calendarDays={calendarDays}
                  order={order}
                  setOrder={setOrder}
                  availableSlots={availableSlots}
                />

                <TaxInvoiceCard order={order} setOrder={setOrder} />

                <ContactPhone order={order} setOrder={setOrder} />

                <OrderNote order={order} setOrder={setOrder} />
              </>
            )}
            <Postscript />
          </div>

          {/* Bottom Fixed Button */}
          <View
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg={"#FFF"}
            borderTopWidth={1}
            borderTopColor={colors.dividerGray}
            p={"$4"}
            shadowColor="#000"
            shadowOffset={{ width: 0, height: -2 }}
            shadowOpacity={0.1}
            shadowRadius={4}
            zIndex={1000000}>
            {isEditMode && (
              <div className="flex">
                <Button
                  {...basicStyle.btnShareStyle}
                  borderColor={colors.darkRed || "#DC2626"}
                  bg={"#FFF"}
                  color={colors.darkRed || "#DC2626"}
                  size={"$5"}
                  onPress={handleCancel}
                  flex={1}>
                  取消訂單
                </Button>
                <Button
                  {...basicStyle.ShareBlueButton}
                  className="primary-bg-gradient"
                  color={"#FFF"}
                  size={"$5"}
                  onPress={handleSubmit}
                  flex={1}
                  disabled={isInvoiceOrPhoneInvalid}
                  opacity={isInvoiceOrPhoneInvalid ? 0.6 : 1}>
                  確認修改
                </Button>
              </div>
            )}
            {isCreate && (
              <Button
                {...basicStyle.ShareBlueButton}
                className="primary-bg-gradient"
                color={"#FFF"}
                size={"$5"}
                onPress={handleSubmit}
                disabled={isInvoiceOrPhoneInvalid}
                opacity={isInvoiceOrPhoneInvalid ? 0.6 : 1}>
                確認新增預約
              </Button>
            )}
          </View>
        </div>
      )}
    </div>
  );
}
